import React, { useContext, useEffect, useState } from 'react';
import {
    Box, Typography, TextField, Button, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Paper,
    IconButton, Avatar, Select, MenuItem, FormControl, InputLabel,
    CircularProgress, Tooltip, Pagination, Grid,
    Divider
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import axios from 'axios';
import apiUrl from '../../Config';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Context from '../../context/Context';
import DeleteConfirm from '../../Common/DeleteConfirm';

const DisplayVehicleBrands = () => {
    const { isConfirmDeleteModelOpen, setIsConfirmDeleteModelOpen, itemIdToDelete,
        confirmDelete } = useContext(Context);
    const [vehicleBrands, setVehicleBrands] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(5);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [error, setError] = useState('')

    const navigate = useNavigate();

    const fetchVehicleBrands = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${apiUrl}/vehiclebrand/getallbrands`, {
                params: {
                    search,
                    pageNumber: page,
                    pageSize: limit
                }
            });
            if (res?.data?.status === true) {

                setVehicleBrands(res.data.brands || []);
                setTotalItems(res.data.totalItems || 0);
                setTotalPages(res.data.totalPages || 1);
            }
        } catch (err) {
            setError("Error fetching vehicle brands:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVehicleBrands();
    }, [page, limit]);

    const handleSearch = () => {
        setPage(1);
        fetchVehicleBrands();
    };

    const handleDelete = async () => {
        setIsConfirmDeleteModelOpen(false);
        setLoading(true)
        try {
            const response = await axios.delete(`${apiUrl}/vehiclebrand/deletebrand/${itemIdToDelete}`)
            if (response.data.status == true) {
                fetchVehicleBrands()
                toast.success(response.data.message)
            }
        } catch (error) {
            setError(error, "error")
        }
        finally {
            setLoading(false)
        }
    };

    const handleAddNew = () => {
        navigate('/create-edit-vehicle-brand');
    };

    const handleEdit = (brand) => {
        navigate('/create-edit-vehicle-brand', { state: brand });
    };

    return (
        <>
            <Box sx={{ p: 3, mt: 1 }}>
                <Grid container spacing={2} justifyContent="space-between" alignItems="center" mb={2}>
                    <Grid item xs={12} md="auto">
                        <Typography variant="h3">Vehicle Brands</Typography>
                    </Grid>
                    <Grid item xs={12} md="auto">
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<Add />}
                            onClick={handleAddNew}
                        >
                            Add New Brand
                        </Button>
                    </Grid>
                </Grid>
                <Divider sx={{ mb: 2 }} />

                <Box mb={2} sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>

                    <Box sx={{ display: 'flex', gap: 2, width: '80%' }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Search by Name"
                            value={search}
                            sx={{ width: { sm: '60%', xs: '60%', md: '200px', lg: '200px' } }}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <Button

                            variant="contained"
                            onClick={handleSearch}
                            size='medium'
                        >
                            Search
                        </Button>
                    </Box>

                    <Box>
                        <FormControl fullWidth size="small">
                            <InputLabel>Rows</InputLabel>
                            <Select
                                value={limit}
                                label="Rows"
                                sx={{ width: '60px' }}
                                onChange={(e) => {
                                    setLimit(Number(e.target.value));
                                    setPage(1);
                                }}
                            >
                                {[5, 10, 20].map(n => (
                                    <MenuItem key={n} value={n}>{n}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                {/* Table or Loader */}
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={150}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {vehicleBrands.length === 0 ? (
                            <Typography>No brands found.</Typography>
                        ) : (
                            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Logo</TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {vehicleBrands.map((brand) => (
                                            <TableRow key={brand.id}>
                                                <TableCell>
                                                    <Avatar src={brand.brandLogo} variant="square" alt={brand.brandName} />
                                                </TableCell>
                                                <TableCell>{brand.brandName}</TableCell>
                                                <TableCell>{brand.isActive ? 'Active' : 'Inactive'}</TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title="Edit">
                                                        <IconButton onClick={() => handleEdit(brand)} color="">
                                                            <Edit />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton onClick={() => confirmDelete(brand.id)} color="error">
                                                            <Delete />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {/* Pagination */}
                        <Box mt={2} display="flex" justifyContent="center">
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(e, value) => setPage(value)}
                                color="primary"
                                showFirstButton
                                showLastButton
                                siblingCount={0}
                                boundaryCount={1}
                                sx={{
                                    '& .MuiPaginationItem-root': {
                                        height: 32,
                                        minWidth: 32,
                                        fontSize: '0.875rem'
                                    }
                                }}
                            />
                        </Box>
                    </>
                )}
            </Box>

            <DeleteConfirm open={isConfirmDeleteModelOpen} onClose={() => setIsConfirmDeleteModelOpen(false)} onConfirm={handleDelete} />
        </>
    );
};

export default DisplayVehicleBrands;
